import { useState, useRef, useEffect } from "react";
import { useMCPServers } from "@/hooks/useMCPServers";
import { mcpServersStore } from "@/store/zustand/mcpServersStore";
import { toast } from "sonner";
import { track } from "@/utils/events/tracker";

interface UseMCPConnectionProps {
  onSuccess?: () => void;
}

export function useMCPConnection({ onSuccess }: UseMCPConnectionProps = {}) {
  const { addServer, fetchServers, initiateOAuth } = useMCPServers();

  const [isConnecting, setIsConnecting] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutHandleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (timeoutHandleRef.current) clearTimeout(timeoutHandleRef.current);
    };
  }, []);

  const handleOAuthFlow = async (
    connectionId: string,
    serverName: string,
  ): Promise<void> => {
    try {
      const authorizationUrl = await initiateOAuth(connectionId);

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      track("MCP", {
        step: "oauth_window_opened",
        serverName,
        connectionId,
      });

      const popup = window.open(
        authorizationUrl,
        "oauth",
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      if (!popup) {
        throw new Error("Popup blocked. Please allow popups for this site.");
      }

      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (timeoutHandleRef.current) clearTimeout(timeoutHandleRef.current);

      pollIntervalRef.current = setInterval(async () => {
        await fetchServers();
        const updatedConnection = mcpServersStore
          .getState()
          .servers.find((s) => s.id === connectionId);

        if (updatedConnection?.connection_status === "connected") {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          if (timeoutHandleRef.current) clearTimeout(timeoutHandleRef.current);
          if (!popup.closed) popup.close();
          setIsConnecting(false);
          toast.success(`Successfully connected to ${serverName}`);
          onSuccess?.();
        } else if (updatedConnection?.connection_status === "error") {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          if (timeoutHandleRef.current) clearTimeout(timeoutHandleRef.current);
          if (!popup.closed) popup.close();
          setIsConnecting(false);
          toast.error(`Failed to connect to ${serverName}`, {
            description: "OAuth authorization failed",
          });
        }
      }, 2000);

      timeoutHandleRef.current = setTimeout(
        () => {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          if (!popup.closed) popup.close();
          setIsConnecting(false);
          toast.error("Authorization timeout. Please try again.");
        },
        5 * 60 * 1000,
      );
    } catch (oauthErr: any) {
      console.error("OAuth initiation failed:", oauthErr);
      toast.error(`Failed to connect to ${serverName}`, {
        description: oauthErr.message || "Failed to initiate OAuth",
      });
      setIsConnecting(false);
    }
  };

  const connectToServer = async (
    serverUrl: string,
    serverName: string,
    transportType: "http" | "sse" = "http",
  ) => {
    setIsConnecting(true);

    try {
      const { connectionId, requiresAuth, status, errorMessage } =
        await addServer(serverUrl, serverName, transportType);

      if (requiresAuth) {
        await handleOAuthFlow(connectionId, serverName);
        return;
      }

      await fetchServers();
      setIsConnecting(false);

      if (status === "error") {
        toast.error(`Failed to connect to ${serverName}`, {
          description:
            errorMessage ||
            "Cannot reach server. Make sure it's running and accessible.",
        });
      } else {
        toast.success(`Successfully connected to ${serverName}`);
        onSuccess?.();
      }
    } catch (err: any) {
      console.error(`Error connecting to ${serverName}:`, err);
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to connect";

      toast.error(`Failed to connect to ${serverName}`, {
        description: errorMessage,
      });
      setIsConnecting(false);
    }
  };

  return {
    isConnecting,
    connectToServer,
  };
}
